import React, { useMemo } from 'react';
import { CountBadge, FlexyTabs, TabStructure } from '@europrocurement/flexy-components';
import { Box } from '@mui/material';
import {
    DocumentPreviewOverlay,
    DossierNotesTab,
    DossierTasksTab,
    DossierHistoryTab,
    DocumentPreviewOverlayWithActions,
    DocumentSignTab,
    DossierFormulaireProvider,
    DossierSignDetailOverlay,
    useDossierDocumentsCtx,
    useCurrentDossierCtx,
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_TO_CHECK,
    CounterTabNotification,
    DossierNotesTabTitle,
    DossierTasksTabTitle,
    DossierTasksProvider,
} from '@europrocurement/l2d-modules';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { DOC_TYPE_DOWNLOADABLE, DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import FormulaireTab from '../FormulaireTab/FormulaireTab';
import InformationsTab from '../InformationsTab/InformationsTab';
import { MessageTab } from '../MessageTab/MessageTab';
import FacturationTab from '../FacturationTab/FacturationTab';
import DocumentsTabs from '../DocumentsTabs/DocumentsTabs';

const PREPARATION_STATUSES = [
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_TO_CHECK,
];
const overlayPaddingTop = { paddingTop: '120px' };

const DossierBody: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const { getMissingDocumentsCountByCategory } = useDossierDocumentsCtx();

    const missingDocumentsCount = getMissingDocumentsCountByCategory({
        categoryId: DOC_TYPE_REQUIRED,
        dossierId: dossier?.id as number,
    });
    const downloadableDocuments = getMissingDocumentsCountByCategory({
        categoryId: DOC_TYPE_DOWNLOADABLE,
        dossierId: dossier?.id as number,
    });

    const collectionIds = useMemo(() => {
        if (!dossier?.collectionId) {
            return [];
        }

        return [dossier.collectionId];
    }, [dossier?.collectionId]);

    const dossierFormulaireIds = useMemo(
        () => (dossier?.id ? [dossier.id as number] : []),
        [dossier?.id],
    );

    const showFormulaireTab = useMemo(() => {
        const isPreparationStatus = PREPARATION_STATUSES.includes(dossier?.statutId as number);
        const hasJsonv1 = !!(dossier as Record<string, unknown>)?.jsonv1;
        return isPreparationStatus || hasJsonv1;
    }, [dossier]);

    const isDeleted = dossier?.deleted === true;

    const tabs: Array<TabStructure> = [
        ...(!isDeleted
            ? [
                  {
                      tabName: 'details',
                      tabTitle: 'Détails du dossier',
                      tabPanel: (
                          <>
                              <InformationsTab />
                              <DocumentPreviewOverlay sxProps={overlayPaddingTop} />
                          </>
                      ),
                  },
              ]
            : []),
        ...(showFormulaireTab
            ? [
                  {
                      tabName: 'formulaire',
                      tabTitle: 'Formulaire',
                      tabPanel: <FormulaireTab />,
                  },
              ]
            : []),
        ...(!isDeleted
            ? [
                  {
                      tabName: 'facturation',
                      tabTitle: 'Facturation',
                      tabPanel: (
                          <>
                              <FacturationTab initialCollectionsIds={collectionIds} />
                              <DocumentPreviewOverlay sxProps={overlayPaddingTop} />
                          </>
                      ),
                  },
              ]
            : []),
        ...(!isDeleted
            ? [
                  {
                      tabName: 'documents',
                      tabTitle: (
                          <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              Documents{' '}
                              {missingDocumentsCount > 0 && (
                                  <Box sx={{ display: 'flex' }}>
                                      <FaOptionIcon
                                          sx={{
                                              lineHeight: 0,
                                              display: 'flex',
                                              alignItems: 'center',
                                              fontSize: '14px',
                                          }}
                                          icon={['fasl', 'file-exclamation']}
                                      />
                                      <CountBadge
                                          count={missingDocumentsCount}
                                          color="warning"
                                          bgVariant="main"
                                          contrastText
                                      />
                                  </Box>
                              )}
                              {downloadableDocuments > 0 && (
                                  <Box sx={{ display: 'flex' }}>
                                      <FaOptionIcon
                                          sx={{
                                              lineHeight: 0,
                                              display: 'flex',
                                              alignItems: 'center',
                                              fontSize: '14px',
                                          }}
                                          icon={['fasl', 'file-import']}
                                      />
                                      <CountBadge
                                          count={downloadableDocuments}
                                          color="warning"
                                          bgVariant="main"
                                          contrastText
                                      />
                                  </Box>
                              )}
                          </Box>
                      ),
                      tabPanel: (
                          <>
                              <DocumentsTabs />
                              <DocumentPreviewOverlayWithActions />
                          </>
                      ),
                  },
              ]
            : []),
        {
            tabName: 'message',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Messages <CounterTabNotification dossier={dossier} />
                </Box>
            ),
            tabPanel: (
                <>
                    <MessageTab />
                    <DocumentPreviewOverlay sxProps={overlayPaddingTop} />
                </>
            ),
        },
        {
            tabName: 'notes',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Notes <DossierNotesTabTitle />
                </Box>
            ),
            tabPanel: <DossierNotesTab requestLogActionLimited={false} />,
        },
        {
            tabName: 'taches',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Tâches <DossierTasksTabTitle />
                </Box>
            ),
            tabPanel: <DossierTasksTab />,
        },
        {
            tabName: 'historique',
            tabTitle: 'Historique',
            tabPanel: (
                <DossierHistoryTab
                    showTechniqueLogs
                    requestLogActionLimited={false}
                />
            ),
        },
        ...(!isDeleted
            ? [
                  {
                      tabName: 'signatures-electronique',
                      tabTitle: 'Signature électronique',
                      tabPanel: (
                          <>
                              <DocumentSignTab />
                              <DossierSignDetailOverlay sxProps={overlayPaddingTop} />
                              <DocumentPreviewOverlay sxProps={overlayPaddingTop} />
                          </>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <DossierFormulaireProvider idDossiers={dossierFormulaireIds}>
            <DossierTasksProvider>
                <FlexyTabs
                    routerParameterName="dossier-detail"
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                    }}
                    tabs={tabs}
                    scrollbar={false}
                    indicatorColor="primary"
                    stickyTabs
                    stickyTop={0}
                />
            </DossierTasksProvider>
        </DossierFormulaireProvider>
    );
};

export default DossierBody;
